import React from 'react';
import {
  CalendarOutlined,
  ShoppingCartOutlined,
  ScheduleOutlined,
  MailOutlined,
  LikeOutlined,
  BarChartOutlined,
  SettingOutlined,
  ShopOutlined,
  CarryOutOutlined,
  ApartmentOutlined,
  TeamOutlined,
  BlockOutlined,
  UserOutlined,
  UnorderedListOutlined,
  NotificationOutlined,
  CommentOutlined,
  FieldTimeOutlined,
  DashboardOutlined,
  CreditCardOutlined,
  FundProjectionScreenOutlined,
  ToolOutlined,
  EnvironmentOutlined,
  LockOutlined,
  HomeOutlined,
  GlobalOutlined,
} from '@ant-design/icons';
import { WSB_MERCHANT_SOURCE } from '../../../shared/constants/merchant';

export interface MenuItem {
  key: string;
  icon?: React.ReactNode;
  label: React.ReactNode;
  children?: MenuItem[];
  className?: string;
}

export interface MaybeLockedMenuItem {
  key: string;
  icon: React.ReactNode;
  label: string;
  translate: (key: string) => string;
  isLocked: boolean;
  isReseller?: boolean;
  isTrialUser?: boolean;
  isWhitelabel?: boolean;
}

const createMaybeLockedMenuItem = ({
  key,
  icon,
  label,
  translate,
  isLocked = false,
  isReseller = false,
  isTrialUser = false,
  isWhitelabel = false,
}: MaybeLockedMenuItem): MenuItem[] => {
  if (isTrialUser) return [];
  if (isLocked && (isWhitelabel || isReseller)) return [];
  return [
    {
      key,
      icon,
      className: isLocked ? 'menu-item-locked' : '',
      label: (
        <span>
          {translate(label)}
          {isLocked && (
            <LockOutlined
              style={{
                fontSize: 12,
                marginLeft: 4,
                cursor: 'pointer',
              }}
            />
          )}
        </span>
      ),
    },
  ];
};

const getDashboardMenuItem = (config: MenuConfiguration): MenuItem[] => {
  const { sessionPermissions, translate } = config;
  if (!sessionPermissions?.dashboard?.available) return [];
  return [
    {
      key: '#/dashboard',
      icon: <HomeOutlined />,
      label: translate('side-menu.home'),
    },
  ];
};

const getCalendarMenuItem = (config: MenuConfiguration): MenuItem[] => {
  const { sessionPermissions, translate } = config;
  if (!sessionPermissions?.calendar?.available) return [];
  return [
    {
      key: '/calendar',
      icon: <CalendarOutlined />,
      label: translate('side-menu.calendar'),
    },
  ];
};

const getPOSManagerMenuItem = (config: MenuConfiguration): MenuItem[] => {
  const {
    isWhitelabel,
    isReseller,
    posManagerUrl,
    isPOSUpsellActive,
    isPOSExternalLinkActive,
    translate,
  } = config;

  return isPOSExternalLinkActive
    ? createMaybeLockedMenuItem({
        key: isPOSUpsellActive ? '/upsell/shorepos' : posManagerUrl,
        icon: <ShoppingCartOutlined />,
        label: 'side-menu.shore-pos',
        translate,
        isLocked: isPOSUpsellActive,
        isWhitelabel,
        isReseller,
      })
    : [];
};

const getBookingPageMenuItem = (config: MenuConfiguration): MenuItem[] => {
  const { merchantSlug, bookingPageUrl, translate } = config;
  if (!merchantSlug) return [];
  return [
    {
      key: bookingPageUrl,
      icon: <ShopOutlined />,
      label: translate('side-menu.booking-page'),
    },
  ];
};

const getShiftPlanMenuItem = (config: MenuConfiguration): MenuItem[] => {
  const {
    isTrialUser,
    isWhitelabel,
    isReseller,
    sessionPermissions,
    translate,
  } = config;
  if (isTrialUser) return [];
  const isLocked = sessionPermissions?.shiftplan?.available === false;
  const key = sessionPermissions?.shiftplan?.available
    ? '/shiftplan'
    : '/upsell/shiftplan';

  return createMaybeLockedMenuItem({
    key,
    icon: <CarryOutOutlined />,
    label: 'side-menu.shift-plan',
    translate,
    isLocked,
    isWhitelabel,
    isReseller,
  });
};

const getManagementMenuItems = (config: MenuConfiguration): MenuItem[] => {
  let children: MenuItem[] = [];
  const { sessionPermissions, translate } = config;
  const isCustomersAppEnabled = sessionPermissions?.customers?.available;
  const isResourcesAppEnabled = sessionPermissions?.resources?.available;
  const isStaffAppEnabled = sessionPermissions?.hr?.available;
  const isServicesAppEnabled = sessionPermissions?.services?.available;

  if (isCustomersAppEnabled) {
    children.push({
      key: '/customers',
      icon: <TeamOutlined />,
      label: translate('side-menu.customers'),
    });
  }
  if (isResourcesAppEnabled) {
    children.push({
      key: '/resources',
      icon: <BlockOutlined />,
      label: translate('side-menu.resources'),
    });
  }
  if (isStaffAppEnabled) {
    children.push({
      key: '/hr',
      icon: <UserOutlined />,
      label: translate('side-menu.employees'),
    });
  }
  if (isServicesAppEnabled) {
    children.push({
      key: '/services',
      icon: <UnorderedListOutlined />,
      label: translate('side-menu.services'),
    });
  }
  if (children.length === 0) return [];
  return [
    {
      key: 'management',
      icon: <ApartmentOutlined />,
      label: translate('side-menu.management'),
      children,
    },
  ];
};

const getMarketingMenuItems = (config: MenuConfiguration): MenuItem[] => {
  const {
    sessionPermissions,
    isTrialUser,
    isWhitelabel,
    isReseller,
    translate,
  } = config;

  const isMessengerAppEnabled = sessionPermissions?.messenger?.available;
  const isNewsletterAppEnabled = sessionPermissions?.newsletter?.available;
  const isFeedbackAppEnabled = sessionPermissions?.feedback?.available;
  const isRemindersAppEnabled = sessionPermissions?.reminders?.available;

  const messengerMenuItem = createMaybeLockedMenuItem({
    key: isMessengerAppEnabled ? '#/messenger' : '/upsell/messenger',
    icon: <CommentOutlined />,
    label: 'side-menu.messenger',
    translate,
    isLocked: !isMessengerAppEnabled,
    isReseller,
    isTrialUser,
    isWhitelabel,
  });

  const newsletterMenuItem = createMaybeLockedMenuItem({
    key: isNewsletterAppEnabled ? '/newsletter' : '/upsell/newsletter',
    icon: <MailOutlined />,
    label: 'side-menu.newsletter',
    translate,
    isLocked: !isNewsletterAppEnabled,
    isReseller,
    isTrialUser,
    isWhitelabel,
  });

  const feedbackMenuItem = isFeedbackAppEnabled
    ? [
        {
          key: '/feedback',
          icon: <LikeOutlined />,
          label: translate('side-menu.feedback'),
        },
      ]
    : [];

  const remindersMenuItem = isRemindersAppEnabled
    ? [
        {
          key: '/reminders',
          icon: <FieldTimeOutlined />,
          label: translate('side-menu.reminders'),
        },
      ]
    : [];

  const children: MenuItem[] = [
    ...messengerMenuItem,
    ...newsletterMenuItem,
    ...feedbackMenuItem,
    ...remindersMenuItem,
  ].filter(Boolean);

  if (children.length === 0) return [];
  return [
    {
      key: 'marketing',
      icon: <NotificationOutlined />,
      label: translate('side-menu.marketing'),
      children,
    },
  ];
};

const getAnalysisMenuItems = (config: MenuConfiguration): MenuItem[] => {
  const {
    sessionPermissions,
    isTrialUser,
    isWhitelabel,
    isReseller,
    translate,
  } = config;

  const isInsightsAppEnabled = sessionPermissions?.insights?.available;
  const isTransactionsAppEnabled = sessionPermissions?.transactions?.available;
  const isReportingsAppEnabled = sessionPermissions?.reportings?.available;

  const insightsMenuItem: MenuItem[] = isInsightsAppEnabled
    ? [
        {
          key: '/insights',
          icon: <DashboardOutlined />,
          label: translate('side-menu.insights'),
        },
      ]
    : [];

  const transactionsMenuItem: MenuItem[] = isTransactionsAppEnabled
    ? [
        {
          key: '/transactions',
          icon: <CreditCardOutlined />,
          label: translate('side-menu.transactions'),
        },
      ]
    : [];

  const posReportingsMenuItem = createMaybeLockedMenuItem({
    key: isReportingsAppEnabled ? '/pos-reports-v2' : '/upsell/reports',
    icon: <FundProjectionScreenOutlined />,
    label: 'side-menu.reports',
    translate,
    isLocked: !isReportingsAppEnabled,
    isReseller,
    isTrialUser,
    isWhitelabel,
  });

  const children: MenuItem[] = [
    ...insightsMenuItem,
    ...transactionsMenuItem,
    ...posReportingsMenuItem,
  ].filter(Boolean);
  if (children.length === 0) return [];

  return [
    {
      key: 'analysis',
      icon: <BarChartOutlined />,
      label: translate('side-menu.analysis'),
      children,
    },
  ];
};

const getSetupMenuItems = (config: MenuConfiguration): MenuItem[] => {
  const { sessionPermissions, hasMultipleLocations, source, translate } =
    config;
  const isSettingsAppEnabled = sessionPermissions?.settings?.available;

  const calendarSettingsMenuItem = isSettingsAppEnabled
    ? [
        {
          key: '/application-settings',
          icon: <ScheduleOutlined />,
          label: translate('side-menu.calendar-settings'),
        },
      ]
    : [];
  const companySettingsMenuItem = isSettingsAppEnabled
    ? [
        {
          key: '/settings',
          icon: <ToolOutlined />,
          label: translate('side-menu.company-settings'),
        },
      ]
    : [];
  const locationSettingsMenuItem = hasMultipleLocations
    ? [
        {
          key: '/locations',
          icon: <EnvironmentOutlined />,
          label: translate('side-menu.location-settings'),
        },
      ]
    : [];
  const userSettingsMenuItem =
    source === WSB_MERCHANT_SOURCE
      ? [
          {
            key: '/user-profile',
            icon: <UserOutlined />,
            label: translate('side-menu.user-settings'),
          },
        ]
      : [];
  const networksMenuItem = sessionPermissions?.networks?.available
    ? [
        {
          key: '/networks',
          icon: <GlobalOutlined />,
          label: translate('side-menu.networks'),
        },
      ]
    : [];

  const children: MenuItem[] = [
    ...userSettingsMenuItem,
    ...calendarSettingsMenuItem,
    ...companySettingsMenuItem,
    ...locationSettingsMenuItem,
    ...networksMenuItem,
  ].filter(Boolean);
  if (children.length === 0) return [];

  return [
    {
      key: 'setup',
      icon: <SettingOutlined />,
      label: translate('side-menu.setup'),
      children,
    },
  ];
};

export interface MenuConfiguration {
  sessionPermissions: any; //TODO: Define proper type
  isTrialUser: boolean;
  isWhitelabel: boolean;
  isReseller: boolean;
  isPOSUpsellActive: boolean;
  isPOSExternalLinkActive: boolean;
  bookingPageUrl: string;
  posManagerUrl: string;
  merchantSlug?: string;
  currentAccountId?: string;
  hasMultipleLocations: boolean;
  source?: string;
  translate: (key: string) => string;
}

export const getMenuItems = (config: MenuConfiguration): MenuItem[] => {
  return [
    ...getDashboardMenuItem(config),
    ...getCalendarMenuItem(config),
    ...getPOSManagerMenuItem(config),
    ...getBookingPageMenuItem(config),
    ...getShiftPlanMenuItem(config),
    ...getManagementMenuItems(config),
    ...getMarketingMenuItems(config),
    ...getAnalysisMenuItems(config),
    ...getSetupMenuItems(config),
  ].filter(Boolean);
};
